import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Launch,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { verifyPaymentStatus, updateCompanyPlan } from '../services/paymentService';
import { clearPendingPlanChange, getPendingPlanChange } from '../services/planUpgradeService';
import { STORAGE_KEYS, PLATFORM_NAME, DISABLE_SUBSCRIPTION } from '../helpers/constants';
import { getZohoSubscriptionId, getZohoCustomerId, getCompanyId, getLoggedInUserData } from '../helpers/storageUtils';
import { useTheme } from '../contexts/ThemeContext';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // When subscription is disabled, redirect to app (user should not land here)
    if (DISABLE_SUBSCRIPTION) {
      navigate('/supportSpace', { replace: true });
      return;
    }
    const handlePaymentSuccess = async () => {
      try {
        const hostedPageId = searchParams.get('hostedpage_id');
        const subscriptionIdFromUrl = searchParams.get('subscription_id');

        if (hostedPageId) {
          // Use REFRESH_TOKEN as primary token (consistent with other services)
          const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
          if (!token) {
            setError('Authentication required to verify payment');
            setLoading(false);
            return;
          }

          // Build validatePayment payload: hostedPageId, subscriptionId, companyId, userId, platform_name
          const companyId = getPendingPlanChange()?.customerId || getCompanyId();
          const userId = user?.id || getLoggedInUserData()?.user_id || '';
          const subscriptionId = getZohoSubscriptionId() || subscriptionIdFromUrl || '';

          const response = await verifyPaymentStatus(
            {
              hostedPageId,
              subscriptionId,
              companyId,
              userId,
              platform_name: PLATFORM_NAME,
            },
            token
          );
          
          if (response.success && (response.status === 'success' || response.status === 'active')) {
            // Check for pending plan change and update company plan
            const pendingPlanChange = getPendingPlanChange();
            if (pendingPlanChange) {
              try {
                // subscriptionId and zohoCustomerId from mySubscriptionData (localStorage)
                const subscriptionIdFromStorage = getZohoSubscriptionId();
                const zohoCustomerId = getZohoCustomerId();
                // Update the company plan in the database
                await updateCompanyPlan({
                  companyId: pendingPlanChange.customerId,
                  newPlanId: pendingPlanChange.planId,
                  subscriptionId: subscriptionIdFromStorage || subscriptionId || undefined,
                  zohoCustomerId: zohoCustomerId || undefined,
                  hostedPageId: hostedPageId || undefined
                }, token);
                
                // Clear the pending plan change
                clearPendingPlanChange();
              } catch (planUpdateError: any) {
                console.error('Error updating company plan:', planUpdateError);
                // Don't show error to user as payment was successful
              }
            }
            
            // Start countdown for auto-redirect
            startCountdown();
          } else {
            console.error('Payment verification failed:', response);
            setError(`Payment verification failed. Status: ${response.status}, Message: ${response.message}. Please contact support.`);
          }
        } else {
          setError('Payment verification failed: Missing payment reference');
        }
      } catch (error: any) {
        console.error('Error handling payment success:', error);
        setError('Failed to verify payment. Please contact support.');
      } finally {
        setLoading(false);
      }
    };

    handlePaymentSuccess();
  }, [searchParams, user, navigate]);

  const startCountdown = () => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/supportSpace');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleGoToApp = () => {
    navigate('/supportSpace');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Processing your payment...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we confirm your subscription upgrade.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleGoToApp}>
          Go to App
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'white',
        }}
      >
        {/* Success Icon */}
        <Box sx={{ mb: 3 }}>
          <CheckCircle
            sx={{
              fontSize: 80,
              color: 'success.main',
              mb: 2,
            }}
          />
        </Box>

        {/* Success Message */}
        <Typography variant="h4" component="h1" gutterBottom>
          Payment Successful!
        </Typography>
        <Typography variant="h6" color="textSecondary" paragraph>
          Thank you for upgrading your subscription
        </Typography>

        {/* Auto-redirect message */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" color="textSecondary">
            You will be automatically redirected to the app in {countdown} seconds
          </Typography>
        </Box>

        {/* Action Button */}
        <Button
          variant="contained"
          startIcon={<Launch />}
          onClick={handleGoToApp}
          size="large"
          sx={{ 
            py: 1.5,
            px: 4,
            textTransform: 'none',
            fontSize: '1.1rem',
          }}
        >
          Go to App
        </Button>
      </Paper>
    </Container>
  );
};

export default PaymentSuccess;
